import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js';

function VoxelShapeCanvas({ label, voxels }) {
    const mountRef = useRef(null);

    useEffect(() => {
        const mount = mountRef.current;
        if (!mount || !voxels?.length) {
            return undefined;
        }

        let renderer;
        try {
            renderer = new THREE.WebGLRenderer({
                alpha: true,
                antialias: true,
                powerPreference: 'high-performance',
                preserveDrawingBuffer: true,
            });
        } catch (error) {
            mount.dataset.renderState = 'failed';
            return undefined;
        }

        renderer.setClearColor(0x000000, 0);
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
        renderer.outputColorSpace = THREE.SRGBColorSpace;
        renderer.domElement.setAttribute('aria-hidden', 'true');
        renderer.domElement.dataset.voxelCanvas = label;
        mount.replaceChildren(renderer.domElement);

        const scene = new THREE.Scene();
        const camera = new THREE.OrthographicCamera(-3, 3, 3, -3, 0.1, 100);
        camera.position.set(7, 8, 9);
        camera.lookAt(0, 0, 0);

        const hemisphereLight = new THREE.HemisphereLight(0xffffff, 0xb9c6dc, 2.35);
        const keyLight = new THREE.DirectionalLight(0xffffff, 3.4);
        const fillLight = new THREE.DirectionalLight(0x9fd9ff, 1.15);
        keyLight.position.set(5, 8, 10);
        fillLight.position.set(-7, 2, 5);
        scene.add(hemisphereLight, keyLight, fillLight);

        const group = new THREE.Group();
        const cubeGeometry = new RoundedBoxGeometry(0.92, 0.92, 0.92, 4, 0.075);
        const edgeGeometry = new THREE.EdgesGeometry(cubeGeometry, 24);
        const cubeMaterial = new THREE.MeshStandardMaterial({
            color: 0x4f7fe8,
            metalness: 0.04,
            roughness: 0.42,
        });
        const edgeMaterial = new THREE.LineBasicMaterial({
            color: 0x183f82,
            opacity: 0.34,
            transparent: true,
        });

        voxels.forEach(([x, y, z]) => {
            const cube = new THREE.Mesh(cubeGeometry, cubeMaterial);
            const edges = new THREE.LineSegments(edgeGeometry, edgeMaterial);
            cube.position.set(x, y, z);
            edges.position.copy(cube.position);
            group.add(cube, edges);
        });

        scene.add(group);
        const bounds = new THREE.Box3().setFromObject(group);
        const center = bounds.getCenter(new THREE.Vector3());
        const size = bounds.getSize(new THREE.Vector3());
        group.position.sub(center);

        let animationFrame = 0;
        let resizeObserver;
        const startedAt = performance.now();
        const render = (now = performance.now()) => {
            const progress = Math.min(1, (now - startedAt) / 420);
            const eased = 1 - ((1 - progress) ** 3);
            const scale = 0.88 + (0.12 * eased);
            group.scale.setScalar(scale);
            group.rotation.y = -0.18 * (1 - eased);
            renderer.render(scene, camera);
            mount.dataset.renderState = 'ready';
            if (progress < 1) {
                animationFrame = window.requestAnimationFrame(render);
            }
        };

        const resize = () => {
            const width = Math.max(1, mount.clientWidth);
            const height = Math.max(1, mount.clientHeight);
            const aspect = width / height;
            const shapeSpan = Math.max(size.x, size.y, size.z, 2);
            const viewHeight = shapeSpan * 1.48;
            camera.left = -(viewHeight * aspect) / 2;
            camera.right = (viewHeight * aspect) / 2;
            camera.top = viewHeight / 2;
            camera.bottom = -viewHeight / 2;
            camera.updateProjectionMatrix();
            renderer.setSize(width, height, false);
            renderer.render(scene, camera);
        };

        resize();
        animationFrame = window.requestAnimationFrame(render);
        if (typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(resize);
            resizeObserver.observe(mount);
        } else {
            window.addEventListener('resize', resize);
        }

        return () => {
            window.cancelAnimationFrame(animationFrame);
            resizeObserver?.disconnect();
            if (!resizeObserver) {
                window.removeEventListener('resize', resize);
            }
            cubeGeometry.dispose();
            edgeGeometry.dispose();
            cubeMaterial.dispose();
            edgeMaterial.dispose();
            renderer.dispose();
            renderer.forceContextLoss();
            renderer.domElement.remove();
        };
    }, [label, voxels]);

    return (
        <div
            aria-hidden="true"
            className="UFOV-brain-games__voxel-view"
            data-voxel-count={voxels.length}
            ref={mountRef}
        />
    );
}

export default VoxelShapeCanvas;
